import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '@/lib/axios';
import {
  addLocalCartItem,
  addLocalCartItemQuantity,
  getLocalCart,
  removeAllLocalCart,
  removeLocalCartItem,
} from './localCart';
import { RootState } from '../store';

export interface CartItem {
  id: string;
  owner_id: number;
  preorder_available: number;
  user_id: number;
  product_id: number;
  slug: string;
  product_name: string;
  product_thumbnail_image: string | null;
  is_preorder: number;
  variation: any; // use a more specific type if known
  price: number;
  currency_symbol: string;
  tax: number;
  shipping_cost: number;
  quantity: number;
  lower_limit: number;
  upper_limit: number;
  influencer_id?: number;
  preorder_delivery_date: number;
}

interface CartState {
  items: CartItem[];
  loading: boolean;
  error: string | null;
}

const initialState: CartState = {
  items: [],
  loading: false,
  error: null,
};

// Async Thunks
export const fetchCartItems = createAsyncThunk(
  'cart/fetchCartItems',
  async (_, { getState }) => {
    const state = getState() as RootState;
    if (state.user.isLoggedIn) {
      const response = await axios.post('/api/v2/carts');
      return response.data[0].cart_items;
    } else {
      return getLocalCart();
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (
    {
      id,
      userId,
      quantity,
      preorder_available,
      influencer_id,
      ...props
    }: {
      influencer_id: string;
      id: string;
      userId?: string;
      quantity?: number;
      preorder_available: boolean;
    },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;

    if (state.user.isLoggedIn) {
      const response = await axios.post('/api/v2/web/carts/add', {
        id,
        user_id: userId,
        quantity: quantity || 1,
        is_preorder: preorder_available,
        influencer_id,
      });

      await dispatch(fetchCartItems());
      return response.data; // Return data
    } else {
      const product = {
        id,
        userId,
        quantity,
        preorder_available,
        influencer_id,
        ...props,
      };

      addLocalCartItem(product, quantity || 1);
      await dispatch(fetchCartItems());
      return {
        result: true,
        message: 'Product added to cart successfully',
        cart_quantity: quantity || 1,
      };
    }
  }
);

export const updateCartQuantity = createAsyncThunk(
  'cart/updateQuantity',
  async ({ id, quantity }: { id: string; quantity: number }, { getState }) => {
    const state = getState() as RootState;
    if (state.user.isLoggedIn) {
      await axios.post('/api/v2/carts/change-quantity', {
        id,
        quantity,
      });
      return { id, quantity };
    } else {
      addLocalCartItemQuantity(Number(id), quantity);
      return { id, quantity };
    }
  }
);

export const deleteCartItem = createAsyncThunk(
  'cart/deleteItem',
  async (id: string, { getState }) => {
    const state = getState() as RootState;

    if (state.user.isLoggedIn) {
      await axios.delete(`/api/v2/carts/${id}`);
      return id;
    } else {
      removeLocalCartItem(Number(id));
      return id;
    }
  }
);

// Redux slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCart: (state) => {
      removeAllLocalCart();
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCartItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchCartItems.fulfilled,
        (state, action: PayloadAction<CartItem[]>) => {
          state.loading = false;
          state.items = action.payload;
        }
      )
      .addCase(fetchCartItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch cart items';
      })

      .addCase(
        addToCart.fulfilled,
        (state, action: PayloadAction<CartItem>) => {}
      )
      .addCase(
        updateCartQuantity.fulfilled,
        (state, action: PayloadAction<{ id: string; quantity: number }>) => {
          const item = state.items.find(
            (item) => item.id === action.payload.id
          );
          if (item) {
            item.quantity = action.payload.quantity;
          }
        }
      )

      .addCase(
        deleteCartItem.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.items = state.items.filter(
            (item) => item.id !== action.payload
          );
        }
      );
  },
});

export const { clearCart } = cartSlice.actions;
export default cartSlice.reducer;
